import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import moment from 'moment';

import { getAsset } from '../../../../../../../util/assets';
import {
  isSearchSubscriptionActiveTrial,
  searchSubscriptionMeta,
} from '../../../PhotoSearchCheckout/helpers';
import CheckmarkIcon from '../../../../../../../components/icons/CheckmarkIcon';
import Button from '../../../../../../../components/global/Button';
import { formatEnumValue } from '../../../../../../../util/text';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import { searchSubscriptionCancel as searchSubscriptionCancelMutation } from '../../../../../../../graphql/mutations/checkout';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import useRequestConfirm from '../../../../../../../hooks/useRequestConfirm';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import { AccountPhotoSearchCheckout } from '../../../../../../../graphql/queries/account';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../../../components/global/StripeCheckout';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../../constants/account-referrals';
import { PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL } from '../../../../../../pro/views/Album/AlbumCreateWizard/AlbumCreateDeliveryPreferenceStep/PhotoManagerCreditCardCaptureModal';

const propTypes = {
  searchSubscriptionItem: PropTypes.shape({
    plan: PropTypes.string,
  }),
  onSelected: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  className: PropTypes.string,
  activeSearchSubscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billing_period: PropTypes.string.isRequired,
    payment_processor: PropTypes.string.isRequired,
    plan: PropTypes.string.isRequired,
    cancelled_at: PropTypes.string.isRequired,
    expires_at: PropTypes.string.isRequired,
    trial_days: PropTypes.number,
  }).isRequired,
  searchSubscriptionCancel: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  account: PropTypes.shape({
    id: PropTypes.string,
    default_payment_method: PropTypes.shape({
      last_4: PropTypes.string,
    }),
  }),
};

const SearchSubscriptionPlanOverview = ({
  searchSubscriptionItem,
  onSelected,
  selected,
  className,
  activeSearchSubscription,
  searchSubscriptionCancel,
  onCheckout,
  submitting,
  account,
}) => {
  const requestConfirm = useRequestConfirm();
  const openAlert = useOpenAlert();
  const [cancelling, setCancelling] = useState(false);
  const openModal = useOpenModal();

  const isCancelled = !!activeSearchSubscription?.cancelled_at;

  const onCancel = () => {
    requestConfirm({
      message: (
        <Fragment>
          <h4 className="w-100 text-center">Cancel your subscription?</h4>
          <p className="text-bold">
            You will retain all of your matched photos from any events you have
            joined. However, if you have created events and uploaded photos to
            them, you will need to download any photos you want to keep as they
            will be removed from the system 7 days following the expiration of
            your Shutterbug subscription.
          </p>
          <p className="text-bold">
            Your subscription expires on{' '}
            {moment(activeSearchSubscription.expires_at).format(
              'MMMM Do, YYYY',
            )}
            .
          </p>
        </Fragment>
      ),
      confirmText: 'Cancel my subscription',
      cancelText: 'Go Back',
      onConfirm: async () => {
        setCancelling(true);
        try {
          await searchSubscriptionCancel(activeSearchSubscription.id);
        } catch (error) {
          openAlert({
            type: 'error',
            message: getErrorString(error),
          });
        } finally {
          setCancelling(false);
        }
      },
      onCancel: () => {},
    });
  };

  const isActiveSearchSubscription =
    activeSearchSubscription?.plan === searchSubscriptionItem.plan &&
    activeSearchSubscription?.billing_period ===
      searchSubscriptionItem.billing_period;

  const { features, accentColor, icon, isPreferred } = searchSubscriptionMeta[
    searchSubscriptionItem.plan
  ];
  const classes = classNames({
    'photo-search-upsell-plan-option-item': true,
    selected,
    [className]: !!className,
  });

  const [, formattedPlan] = formatEnumValue(searchSubscriptionItem.plan).split(
    ' ',
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={onSelected}
      onClick={onSelected}
      className={classes}
    >
      <div className="photo-search-upsell-plan-option-item-header">
        <div className="d-flex align-items-center">
          <img
            height="30px"
            src={getAsset(icon)}
            alt="plan logo"
            className="mr-1"
          />
          <span className="text-uppercase" style={{ color: accentColor }}>
            {formattedPlan}
          </span>
        </div>
        {isPreferred && (
          <span
            className="text-extra-bold text-xs"
            style={{ color: accentColor }}
          >
            PREFERRED!
          </span>
        )}
      </div>
      <ul>
        {features.map((feature, index) => {
          return (
            <li key={index}>
              <CheckmarkIcon color={accentColor} className="mr-1" size="15px" />
              {feature}
            </li>
          );
        })}
      </ul>

      <div className="photo-search-upsell-footer">
        {isActiveSearchSubscription && (
          <div className="photo-search-upsell-active-plan">
            <p className="text-yellow text-xs text-uppercase">
              Currently Active{' '}
              {/* eslint-disable-next-line no-nested-ternary */}
              {isCancelled
                ? '(Cancelled)'
                : isSearchSubscriptionActiveTrial(activeSearchSubscription)
                ? '(TRIAL)'
                : ''}
            </p>
            <p>
              {isCancelled ? 'Expires at: ' : 'Next billing at: '}
              <span className="text-primary">
                {moment(activeSearchSubscription.expires_at).format(
                  'MM/DD/YYYY',
                )}
              </span>
            </p>
            {account?.default_payment_method && (
              <div className="d-flex align-items-center justify-content-center">
                <p className="mb-0 text-white text-sm">
                  Card ending in &hellip;{' '}
                  {account.default_payment_method.last_4}
                </p>
                <Button
                  btnStyle="link"
                  className="ml-1"
                  btnSize="sm"
                  onClick={() =>
                    openModal(PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL, {
                      title: <h3>Update your credit card</h3>,
                      buttonLabel: 'Update',
                    })
                  }
                >
                  Update Card
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="d-flex mt-2 justify-content-center">
          {activeSearchSubscription && (
            <>
              {isActiveSearchSubscription ? (
                <Button
                  btnStyle="link"
                  className="text-danger"
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onCancel();
                  }}
                  disabled={cancelling || isCancelled}
                  btnSize="sm"
                >
                  {isCancelled ? 'Cancelled' : 'Cancel Plan'}
                </Button>
              ) : (
                (() => {
                  const [name, plan] = formatEnumValue(
                    searchSubscriptionItem.plan,
                  ).split(' ');

                  return (
                    <Button
                      onClick={() => {
                        openModal(STRIPE_CHECKOUT_MODAL, {
                          title: (
                            <h3 className="text-center">
                              Purchase {name} {plan}
                            </h3>
                          ),
                          buttonLabel: `Pay $${formatPriceForDisplay(
                            searchSubscriptionItem?.price.value,
                          )}`,
                          accountReferralTypes: [
                            ACCOUNT_REFERRAL_TYPE.SHUTTERBUG_PROMO,
                            ACCOUNT_REFERRAL_TYPE.TIER_1_SHUTTERBUG_PROMO,
                          ],
                          onSuccess: token =>
                            onCheckout({
                              searchSubscriptionItemId:
                                searchSubscriptionItem.id,
                              token,
                            }),
                          defaultName: account?.name ?? '',
                          defaultEmail: account?.email_address ?? '',
                        });
                      }}
                      disabled={submitting || cancelling}
                      btnSize="sm"
                    >
                      {submitting ? (
                        'Processing...'
                      ) : (
                        <Fragment>Switch to {plan}</Fragment>
                      )}
                    </Button>
                  );
                })()
              )}
            </>
          )}
          <div className="photo-search-upsell-plan-option-item-price">
            <span>
              ${formatPriceForDisplay(searchSubscriptionItem?.price.value)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const withSearchSubscriptionCancel = graphql(searchSubscriptionCancelMutation, {
  props: ({ mutate }) => ({
    searchSubscriptionCancel: id =>
      mutate({
        variables: {
          input: {
            search_subscription_id: id,
          },
        },
        refetchQueries: [
          {
            query: AccountPhotoSearchCheckout,
          },
        ],
      }),
  }),
});

SearchSubscriptionPlanOverview.propTypes = propTypes;

export default compose(withSearchSubscriptionCancel)(
  SearchSubscriptionPlanOverview,
);
